import './styles.scss'
import * as React from 'react'
import { bindActionCreators } from 'redux'
import { Collapse, Input } from 'reactstrap'
import { I18n } from 'react-redux-i18n'

import { surveys as surveysApi } from 'api'
import { composerAct } from '../../../../redux/actions'
import { SelectWithLabel } from '../../../Form'
import Nixi1Spinner from 'components/Nixi1Spinner'
import dkvConfiguration from 'constants/dkv.constants'
import { getStaticImage } from 'utils/statics'
import { isHex } from 'utils/Validations'

const PluginSurveys = ({ data, dispatch }) => {
  const { _id: _userId, delegation, brand, isRemoveByUser } = data.user

  const composerAction = bindActionCreators(composerAct, dispatch)
  const [cardOpen, setCardOpen] = React.useState(false)
  const [filterOpen, setFilterOpen] = React.useState(false)
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [surveys, setSurveys] = React.useState([])
  const [surveysFiltered, setSurveysFiltered] = React.useState([])
  const [errorMessage, setErrorMessage] = React.useState(null)
  const [type, setType] = React.useState(null)
  const [order, setOrder] = React.useState(null)
  const [search, setSearch] = React.useState('')
  const [isLoadingSelectedSurvey, setIsLoadingSelectedSurvey] = React.useState(false)
  const typeOptions = [
    { value: 'operatorEmail', label: I18n.t('dashboard_operator-email') },
    { value: 'clientId', label: I18n.t('dashboard_customer-id') }
  ]
  const orderOptions = [
    { value: 'asc', label: I18n.t('order_by_az') },
    { value: 'desc', label: I18n.t('order_by_za') }
  ]

  React.useEffect(() => {
    getSurveys(brand, delegation)
  }, [delegation, brand])

  React.useEffect(() => {
    if (surveys.length) setSurveysFiltered(surveys)
  }, [surveys])

  React.useEffect(() => {
    if (surveys.length) {
      if (type) {
        setSurveysFiltered(surveys.filter(survey => survey.type === type.value))
      } else {
        setSurveysFiltered(surveys)
      }
    }
  }, [type])

  React.useEffect(() => {
    if (surveysFiltered.length && order) {
      const sortedSurveysFiltered = [...surveysFiltered]
      sortedSurveysFiltered.sort((a, b) => order.value === 'asc' ? a.name.localeCompare(b.name) : a.name.localeCompare(b.name) * -1 )
      setSurveysFiltered(sortedSurveysFiltered)
    }
  }, [order])

  React.useEffect(() => {
    if (surveys.length && search.length) {
      setSurveysFiltered(surveys.filter(survey => survey.name.toLowerCase().includes(search.toLowerCase())))
    } else {
      setSurveysFiltered(surveys)
    }
  }, [search])

  React.useEffect(() => {
    if (surveys.length && !surveysFiltered.length) {
      setErrorMessage(I18n.t('surveys_not_data'))
    } else {
      setErrorMessage(null)
    }
  }, [surveysFiltered])

  const getSurveys = async (brand, delegation) => {
    setErrorMessage(null)
    if (!isHex(brand) || !isHex(delegation)) return

    try {
      const { data } = await surveysApi.get({
        withoutCenters: true,
        brands: [brand],
        delegations: [delegation]
      })
      setSurveys(data)
    } catch (error) {
      console.error(error.message)
    }
  }

  const onChange = async (value, type) => {
    if (type === 'type') {
      setType(value)
    }
    else if (type === 'sort') {
      setOrder(value)
    }
    else if (type === 'search') {
      setSearch(value)
    }
  }

  const toggleCard = () => {
    if (!cardOpen) {
      setCardOpen(isRemoveByUser ? false : true)
    } else {
      setCardOpen(false)
      setFilterOpen(false)
    }
  }

  const toggleFilter = () => {
    if (!cardOpen) {
      setCardOpen(isRemoveByUser ? false : true)
    }
    setSearchOpen(false)
    setFilterOpen(!filterOpen)
  }

  const toggleSearch = () => {
    if (!cardOpen) {
      setCardOpen(isRemoveByUser ? false : true)
    }
    setFilterOpen(false)
    setSearchOpen(!searchOpen)
  }

  const selectSurvey = async ({ _id: surveyId }) => {
    setIsLoadingSelectedSurvey({ [surveyId]: true })
    const { data } = await surveysApi.createUrl({ surveyId, userId: _userId })

    composerAction.addExternalText(data.content)
    composerAction.updateButtons(data.buttons)
    composerAction.setSurvey()

    setIsLoadingSelectedSurvey({ [surveyId]: false })
  }

  return (
    <div className={`plugin-surveys-card ${isRemoveByUser ? 'disabled' : ''}`}>
      <div className='title-container'>
        <div className='title'>
          <figure>
            <img src={getStaticImage('/icons/orange-brand/icon-survey-dark.svg')} alt='Surveys' />
          </figure>
          <p>{I18n.t('dashboard_surveys')}</p>
        </div>
        <div className='action-buttons-container'>
          <i className={`fa fa-filter ${filterOpen && !isRemoveByUser ? 'open-icon' : ''}`} onClick={toggleFilter} />
          <i className={`fa fa-search ${searchOpen && !isRemoveByUser ? 'open-icon' : ''}`} onClick={toggleSearch} />
          <i className={`fa fa-chevron-${cardOpen && !isRemoveByUser ? 'up' : 'down'}`} onClick={toggleCard} />
        </div>
      </div>
      <Collapse isOpen={cardOpen}>
        <div className='surveys-container'>
          <div className='separator'></div>
          <Collapse isOpen={filterOpen}>
            <div className='filter-container'>
              {dkvConfiguration.isDKV ? (
                <SelectWithLabel
                  name='type'
                  key='type'
                  classNameWrapper='select-type'
                  options={typeOptions}
                  value={type}
                  placeholder={I18n.t('dashboard_type')}
                  onChange={value => onChange(value, 'type')}
                  isClearable
                />
              ) : null}
              <SelectWithLabel
                name='sort'
                key='sort'
                classNameWrapper='select-sort'
                options={orderOptions}
                value={order}
                placeholder={I18n.t('dashboard_order-by')}
                onChange={value => onChange(value, 'sort')}
                isClearable
              />
            </div>
          </Collapse>
          <Collapse isOpen={searchOpen}>
            <div className='search-container'>
              <Input
                name='search'
                id='search'
                className='input'
                value={search}
                onChange={({ target: { value } }) => onChange(value, 'search')}
              />
            </div>
          </Collapse>
          <div className='surveys-container'>
            {errorMessage ? <p className='info'>{errorMessage}</p> : null}
            {surveysFiltered.map((survey, index) => (
              <div key={index} className='survey' onClick={() => selectSurvey(survey)}>
                <div className='survey-title'>{survey.name}</div>
                {isLoadingSelectedSurvey[survey._id] ? <Nixi1Spinner width='15px' height='15px' /> : null}
              </div>
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default PluginSurveys
